import FormInputField from "@/components/lib/from-input-field";
import { Table, TableHead, TableHeader, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Layout2 from "@/components/Layout/Layout2";
import { Link, useLocation, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { loyaltyLinks } from "@/components/lib/SidebarLinks";
import { Switch } from "@/components/ui/switch";
import ExceptionDays from "@/components/common/ExceptionDays";
import CustomerMultiSelect from "@/components/loyalty/CustomerMultiSelect";
import { getAllCustomerApi } from "@/api/customer/customer";
import { addConcessionApi, editConcessionApi } from "@/api/concession";
import TableSkeleton from "@/components/skeleton/table-skeletion";
import { useDispatch, useSelector } from "react-redux";
import CreateItem from "@/components/dialog/menu-manage/common-dialog/create-item";
import { clearItemVariants, removeItemVariant, setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { Trash2 } from "lucide-react";
import LoyaltyCreateSkeleton from "@/components/loyalty/LoyaltyCreateSkeleton";

function AddConcession() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updateItem = location.state;
  const { type } = useParams();
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [showIsChild, setShowIsChild] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const { getLoading } = useSelector((state) => state.menuManage.menu);
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);
  
  const form = useForm({
    resolver: zodResolver(addNewVoucherSchema),
    mode: "onChange",
    defaultValues: {
      actionType: "add",
      type: "fixed",
      concession_items: [],
      complain_note: "",
      resolution: "",
      note: "",
      template_id: 1,
      status: "active",
    },
  });
  
  const isFoodItem = form.watch("type") === "food_item" ? true : false;
  const concessionItems = form.watch("concession_items");
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [customer, setCustomer] = useState([])

  const { reset } = form;

  useEffect(() => {
    dispatch(fetchAllFlatMenu(user?.restaurant_id));
    getAllCustomerApi()
      .then((res) => {
        if (res.status === "success") {
          setCustomer(res.data.customers);
          
          if (updateItem) {
            const previousSelectedUser = res.data.customers.find(
              item => item?.unique_id  == updateItem?.customer_id
            );

            setSelectedUser([previousSelectedUser]);
          }
        }
      })
      .catch((err) => console.log(err));
    if (type !== "update") {
      form.reset();
      dispatch(clearItemVariants());
      return;
    }
    if (updateItem) {
      const { type, concession_items, complain_note, resolution, note, template_id, status, exception_days } = updateItem;
      form.reset({
        actionType: "update",
        type: type || "fixed",
        concession_items: concession_items || [],
        complain_note: complain_note || [],
        resolution: resolution || [],
        note: note || "",
        template_id: template_id || 1,
        status: status || "active"
      });

      setSelectedDays(exception_days ? exception_days.map(item=>item.id) : []);

      dispatch(setItemVariants(concession_items || []));
    }
  }, [updateItem]);

  const handleItemUpdate = async (payload, actionType = "add") => {
    if (!payload) return;
    let allItem = [...itemVariants];
    if (actionType === "add") {
      allItem = [...itemVariants, payload];
    } else if (actionType === "remove") {
      allItem = itemVariants.filter((item) => item.unique_id !== payload);
    }
    form.setValue("concession_items", allItem);
  };
  const handleRemove = async (id) => {
    await dispatch(removeItemVariant(id));
    await handleItemUpdate(id, "remove");
    toast.success("Item removed successfully");
  };

  const addVoucherData = async (data) => {
    setLoading(true);
    const payload = {
      ...data,
      exception_days: selectedDays,
      reward: isFoodItem ? null : 50.01,
      restaurant_id: user.restaurant_id,
      customer_id: selectedUser[0]?.id,
      concession_items: isFoodItem ? concessionItems : [],
    };
    
    try {
      let res;

      if (type === "update" && updateItem?.id) {
        res = await editConcessionApi(payload, updateItem.id);
      } else {
        res = await addConcessionApi(payload);
      }

      if (res.status === "success") {
        if (type === "update") {
          toast.success("Concession updated successfully!");
        } else {
          toast.success("Concession added successfully!");
        }

        navigate("/loyalty/concession");
        reset();
        dispatch(clearItemVariants());
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Gift Card");
    } finally {
      setLoading(false);
    }
  };

  if (getLoading) return <LoyaltyCreateSkeleton links={loyaltyLinks} />;

  return (
    <Layout2 LeftBar={<LeftBar2 sidebarLinks={loyaltyLinks} />}>
      <div className="w-full max-w-5xl mx-auto my-5 mb-10 px-4">
        <Card className="bg-cblack4 text-cwhite">
          <CardHeader>
            <CardTitle className=" text-cwhite">Add New Concession</CardTitle>
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-8">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(addVoucherData)}
                  className="flex flex-col gap-8"
                >
                  <FormInputField
                    name="type"
                    style="bg-cblack3"
                    placeholder={"Select reward type"}
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={[
                      { label: "Redeem free item", value: "fixed" },
                      { label: "Redeem Cash Discount", value: "percentage" },
                      { label: "Redeem % Discount", value: "food_item" },
                    ]}
                    label={"Reward type"}
                    required={true}
                  />
                  {isFoodItem && (
                    <div className="border p-5">
                      <div className="flex justify-between items-center gap-5">
                        <h1 className="font-bold">Add Reward</h1>
                        <Button
                          type="button"
                          className="bg-cyellow text-black"
                          onClick={() => setOpenDialog(true)}
                        >
                          Add Reward Item
                        </Button>
                      </div>
                      {itemVariants && itemVariants.length > 0 && (
                        <Table className="mt-4">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item Name</TableHead>
                              <TableHead>Image</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead className="text-right">
                                Action
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {itemVariants && itemVariants.length > 0 ? (
                              itemVariants.map((item, i) => (
                                <TableRow key={i}>
                                  <TableCell>
                                    {item?.item_name ||
                                      item?.menu_item?.name ||
                                      "Data not available"}
                                  </TableCell>
                                  <TableCell>
                                    {item.file_manager_url ? (
                                      <img
                                        src={item.file_manager_url}
                                        alt={item.item_name}
                                        className="w-12 h-12 rounded"
                                      />
                                    ) : (
                                      "No Image"
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {item.price !== undefined
                                      ? `£ ${item.price}`
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <span className="flex items-center justify-end gap-3">
                                      <Trash2
                                        onClick={() =>
                                          handleRemove(item?.unique_id)
                                        }
                                        className="w-4 h-4 text-cred cursor-pointer"
                                      />
                                    </span>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={7} className="text-center">
                                  No data available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 flex-col">
                    <Label>Customer ID</Label>
                    <CustomerMultiSelect
                      options={customer}
                      value={selectedUser}
                      onChange={setSelectedUser}
                      customerId={true}
                    />
                  </div>
                  <FormInputField
                    name="complain_note"
                    style="bg-cblack3"
                    placeholder={"Enter Complain Note"}
                    form={form}
                    type="text"
                    label={"Complain Note"}
                    required={true}
                  />
                  <FormInputField
                    name="resolution"
                    style="bg-cblack3"
                    placeholder={"Select resolution"}
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={[
                      { label: "Replace item next time", value: "Replace item next time" },
                      { label: "Discount for next order", value: "Discount for next order" }
                    ]}
                    label={"Resolution"}
                    required={true}
                  />
                  <ExceptionDays label="Exception day" value={selectedDays} onChange={setSelectedDays} />
                  <FormInputField
                    name="note"
                    style="bg-cblack3"
                    placeholder={"Enter note"}
                    form={form}
                    type="text"
                    label={"Notes"}
                    required={true}
                  />
                  <div className="space-y-4">
                    <div className="flex gap-2 items-center">
                      <Switch
                        className="data-[state=unchecked]:bg-neutral-500"
                        checked={showIsChild}
                        onCheckedChange={(val) => setShowIsChild(val)}
                      />
                      <Label>Select Template</Label>
                    </div>
    
                    {showIsChild && (
                      <FormInputField
                        name="template_id"
                        style="bg-cblack7 flex-1"
                        placeholder="Select template"
                        form={form}
                        type="select"
                        search={true}
                        parseAs="number"
                        selectOption={
                          [
                            {
                              label: "No Menu available",
                              value: "",
                              disabled: true,
                            },
                          ]
                        }
                      />
                    )}
                  </div>
                  
                  <FormInputField
                    name="status"
                    style="bg-cblack3"
                    placeholder={"Select status"}
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={[
                      {
                        label: "Active",
                        value: "active",
                      },
                      { label: "Inactive", value: "inactive" },
                    ]}
                    label={"Status"}
                    required={true}
                  />
                  <div className="mt-4 flex gap-5 w-full">
                    <Link
                      className="w-full"
                      to={"/loyalty/concession"}
                    >
                      <Button className="w-full bg-cred text-white">
                        Cancel
                      </Button>
                    </Link>

                    <Button isLoading={loading} className="w-full bg-cyellow">
                      Confirm
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
        <CreateItem
          title={"Add Course Item"}
          isOpen={openDialog}
          onClose={() => setOpenDialog(false)}
          onSuccess={(item) => handleItemUpdate(item, "add")}
          quantity={false}
        />
      </div>
    </Layout2>
  );
}

export default AddConcession;

const addNewVoucherSchema = z.object({
  type: z.string().min(1, "Reward type is required"),
  note: z.string().min(1, "Note is required"),
  complain_note: z.string().min(1, "Complain Note is required"),
  resolution: z.string().min(1, "Resolution is required"),
  status: z.string().min(1, "Status is required"),
  template_id: z.number().min(1, "Template is required"),
});
